import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { 
  BookOpen, 
  Shield, 
  Eye, 
  MapPin, 
  Lock, 
  Globe,
  AlertTriangle,
  ChevronRight,
  Play,
  FileText,
  Search,
  ExternalLink,
  Star,
  Bookmark,
  Headphones,
  Mail,
  Video,
  Scale,
  Smartphone,
  Server,
  Key,
  MessageSquare,
  Wifi,
  Crown,
  CheckCircle2,
  Users,
  Building,
  Newspaper
} from 'lucide-react';

export default function Education() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('concepts');
  const [selectedConcept, setSelectedConcept] = useState(null);

  // Privacy 101 - Essential Concepts
  const privacyConcepts = [
    {
      title: "What is a Privacy Policy?",
      description: "A legal document that explains how a company collects, uses, and protects your personal data.",
      icon: FileText,
      difficulty: "Beginner",
      readTime: "5 min",
      content: `A privacy policy is a legal document that explains how an organization collects, uses, stores, and protects your personal information. It's required by law in many jurisdictions.

**What it typically covers:**
- What data is collected (name, email, browsing history)
- How data is used (marketing, analytics, personalization)
- Who data is shared with (third parties, advertisers)
- How long data is retained
- Your rights (access, deletion, opt-out)
- Security measures in place

**Why you should read them:**
While lengthy and complex, privacy policies reveal crucial information about how your data is handled. Look for red flags like data selling, excessive collection, or unclear retention periods.

**Key takeaway:** Always review privacy policies before signing up for services, especially for sensitive data like health, finance, or location tracking.`
    },
    {
      title: "Dark Patterns Explained",
      description: "Deceptive design tricks that manipulate users into making unintended choices about their privacy.",
      icon: AlertTriangle,
      difficulty: "Beginner",
      readTime: "8 min",
      content: `Dark patterns are user interface design choices that trick users into doing things they didn't intend, like sharing more data, making purchases, or staying subscribed.

**Common examples:**
- **Confirm-shaming:** Guilt-tripping language like "No, I don't want to save money"
- **Hidden costs:** Extra fees revealed at checkout
- **Disguised ads:** Ads designed to look like content
- **Forced continuity:** Free trial that auto-converts to paid without clear warning
- **Privacy Zuckering:** Tricking users into sharing more data than intended

**In privacy contexts:**
- Pre-checked boxes for marketing emails
- "Accept All" buttons that are bright and prominent while "Reject" is hidden
- Confusing language that makes opting out difficult
- Multiple screens to decline vs one click to accept

**How to protect yourself:**
- Read carefully before clicking
- Look for the "Reject All" option (usually hidden)
- Use browser extensions that block dark patterns
- Report deceptive practices to regulators`
    },
    {
      title: "Zero, First, Second & Third-Party Data",
      description: "Understanding the difference between data types and who collects them.",
      icon: Users,
      difficulty: "Beginner",
      readTime: "6 min"
    },
    {
      title: "What is Consent Fatigue?",
      description: "When users become overwhelmed by constant privacy consent requests and stop reading them.",
      icon: Eye,
      difficulty: "Beginner",
      readTime: "4 min"
    },
    {
      title: "Privacy Impact Assessment (PIA)",
      description: "A process to identify and minimize privacy risks in projects and systems.",
      icon: Scale,
      difficulty: "Intermediate",
      readTime: "10 min"
    },
    {
      title: "Data Subject Access Request (DSAR)",
      description: "Your legal right to request a copy of all personal data a company holds about you.",
      icon: FileText,
      difficulty: "Intermediate",
      readTime: "7 min"
    },
    {
      title: "Privacy Enhancing Technologies (PETs)",
      description: "Technologies designed to protect personal data while still enabling useful data processing.",
      icon: Shield,
      difficulty: "Advanced",
      readTime: "12 min"
    },
    {
      title: "Cross-Device Tracking",
      description: "How companies link your activity across phones, tablets, computers, and smart TVs.",
      icon: Smartphone,
      difficulty: "Intermediate",
      readTime: "8 min"
    }
  ];

  // Recommended Privacy Tools by Category
  const toolCategories = [
    {
      category: "VPN Services",
      icon: Globe,
      description: "Encrypt your internet traffic and hide your IP address",
      tools: [
        { name: "Mullvad VPN", rating: 5, price: "€5/mo", highlight: "No email required, anonymous", link: "https://mullvad.net" },
        { name: "ProtonVPN", rating: 4.5, price: "Free tier available", highlight: "Swiss-based, open source", link: "https://protonvpn.com" },
        { name: "Surfshark", rating: 4.5, price: "$2.49/mo", highlight: "Unlimited devices", link: "https://surfshark.com" },
        { name: "Windscribe", rating: 4, price: "Free 10GB/mo", highlight: "Generous free tier", link: "https://windscribe.com" }
      ]
    },
    {
      category: "Private Browsers",
      icon: Globe,
      description: "Browse the web without being tracked",
      tools: [
        { name: "LibreWolf", rating: 5, price: "Free", highlight: "Privacy-hardened Firefox fork", link: "https://librewolf.net" },
        { name: "Brave Browser", rating: 5, price: "Free", highlight: "Built-in ad blocker", link: "https://brave.com" },
        { name: "Firefox Focus", rating: 4.5, price: "Free", highlight: "Mobile privacy browser", link: "https://mozilla.org/firefox/focus" },
        { name: "Tor Browser", rating: 5, price: "Free", highlight: "Maximum anonymity", link: "https://torproject.org" }
      ]
    },
    {
      category: "Encrypted Email",
      icon: Mail,
      description: "Keep your email communications private",
      tools: [
        { name: "ProtonMail", rating: 5, price: "Free tier", highlight: "Swiss privacy, E2E encrypted", link: "https://proton.me" },
        { name: "StartMail", rating: 4.5, price: "$5/mo", highlight: "Unlimited aliases", link: "https://startmail.com" },
        { name: "Tutanota", rating: 4.5, price: "Free tier", highlight: "German, open source", link: "https://tutanota.com" },
        { name: "Mailfence", rating: 4, price: "Free tier", highlight: "Belgian, integrated calendar", link: "https://mailfence.com" }
      ]
    },
    {
      category: "Password Managers",
      icon: Key,
      description: "Generate and store strong, unique passwords",
      tools: [
        { name: "Bitwarden", rating: 5, price: "Free", highlight: "Open source, self-hostable", link: "https://bitwarden.com" },
        { name: "1Password", rating: 5, price: "$2.99/mo", highlight: "Best UX, family sharing", link: "https://1password.com" },
        { name: "KeePassXC", rating: 4.5, price: "Free", highlight: "Offline, open source", link: "https://keepassxc.org" },
        { name: "Dashlane", rating: 4, price: "$4.99/mo", highlight: "VPN included", link: "https://dashlane.com" }
      ]
    },
    {
      category: "Encrypted Messaging",
      icon: MessageSquare,
      description: "Communicate privately with end-to-end encryption",
      tools: [
        { name: "Signal", rating: 5, price: "Free", highlight: "Gold standard, open source", link: "https://signal.org" },
        { name: "Session", rating: 4.5, price: "Free", highlight: "No phone number needed", link: "https://getsession.org" },
        { name: "Element (Matrix)", rating: 4, price: "Free", highlight: "Decentralized, self-hostable", link: "https://element.io" },
        { name: "Briar", rating: 4, price: "Free", highlight: "Works without internet", link: "https://briarproject.org" }
      ]
    },
    {
      category: "Private Search Engines",
      icon: Search,
      description: "Search the web without being profiled",
      tools: [
        { name: "Brave Search", rating: 5, price: "Free", highlight: "Own index, no tracking", link: "https://search.brave.com" },
        { name: "DuckDuckGo", rating: 4.5, price: "Free", highlight: "Most popular private search", link: "https://duckduckgo.com" },
        { name: "Startpage", rating: 4, price: "Free", highlight: "Google results, no tracking", link: "https://startpage.com" },
        { name: "Searx", rating: 4, price: "Free", highlight: "Self-hostable metasearch", link: "https://searx.github.io/searx" }
      ]
    },
    {
      category: "Data Removal Services",
      icon: Trash2,
      description: "Remove your personal data from data brokers",
      tools: [
        { name: "Incogni", rating: 5, price: "$6.49/mo", highlight: "180+ data brokers", link: "https://incogni.com" },
        { name: "DeleteMe", rating: 4.5, price: "$10.75/mo", highlight: "Established, US-focused", link: "https://joindeleteme.com" },
        { name: "Privacy Bee", rating: 4, price: "$15.99/mo", highlight: "Business & personal", link: "https://privacybee.com" },
        { name: "Kanary", rating: 4, price: "$8/mo", highlight: "Automated monitoring", link: "https://kanary.com" }
      ]
    },
    {
      category: "Secure Cloud Storage",
      icon: Server,
      description: "Store files with zero-knowledge encryption",
      tools: [
        { name: "Proton Drive", rating: 5, price: "Free tier", highlight: "Swiss, E2E encrypted", link: "https://proton.me/drive" },
        { name: "Internxt", rating: 4.5, price: "10GB free", highlight: "Lifetime plans available", link: "https://internxt.com" },
        { name: "Tresorit", rating: 4.5, price: "$10.42/mo", highlight: "Business-focused", link: "https://tresorit.com" },
        { name: "Cryptomator", rating: 4.5, price: "Free", highlight: "Encrypt any cloud storage", link: "https://cryptomator.org" }
      ]
    }
  ];

  // Privacy Laws & Regulations
  const privacyLaws = [
    {
      region: "🌍 Global",
      laws: [
        { name: "GDPR", fullName: "General Data Protection Regulation", jurisdiction: "European Union", year: "2018", description: "The gold standard for data privacy laws worldwide" },
        { name: "LGPD", fullName: "Lei Geral de Proteção de Dados", jurisdiction: "Brazil", year: "2020", description: "Brazil's comprehensive data protection law" }
      ]
    },
    {
      region: "🇺🇸 United States",
      laws: [
        { name: "CCPA/CPRA", fullName: "California Privacy Rights Act", jurisdiction: "California", year: "2023", description: "Most comprehensive US state privacy law" },
        { name: "VCDPA", fullName: "Virginia Consumer Data Protection Act", jurisdiction: "Virginia", year: "2023", description: "Virginia's comprehensive privacy law" },
        { name: "CPA", fullName: "Colorado Privacy Act", jurisdiction: "Colorado", year: "2023", description: "Colorado's consumer privacy law" },
        { name: "CTDPA", fullName: "Connecticut Data Privacy Act", jurisdiction: "Connecticut", year: "2023", description: "Connecticut's data privacy law" },
        { name: "TDPSA", fullName: "Texas Data Privacy and Security Act", jurisdiction: "Texas", year: "2024", description: "Texas comprehensive privacy law" }
      ]
    },
    {
      region: "🌏 Asia-Pacific",
      laws: [
        { name: "PDPA", fullName: "Personal Data Protection Act", jurisdiction: "Singapore", year: "2012", description: "Singapore's data protection framework" },
        { name: "APPI", fullName: "Act on Protection of Personal Information", jurisdiction: "Japan", year: "2017", description: "Japan's personal data protection law" },
        { name: "DPDP", fullName: "Digital Personal Data Protection Act", jurisdiction: "India", year: "2023", description: "India's new comprehensive privacy law" }
      ]
    },
    {
      region: "🇬🇧 United Kingdom",
      laws: [
        { name: "UK GDPR", fullName: "UK General Data Protection Regulation", jurisdiction: "United Kingdom", year: "2021", description: "Post-Brexit version of GDPR" },
        { name: "DUAA", fullName: "Data Use and Access Act", jurisdiction: "United Kingdom", year: "2025", description: "New UK data legislation" }
      ]
    }
  ];

  // Learning Resources
  const learningResources = [
    {
      type: "Podcasts",
      icon: Headphones,
      items: [
        { name: "Privacy, Security, & OSINT Show", host: "Michael Bazzell", description: "Deep dives into privacy techniques", link: "https://inteltechniques.com/podcast.html" },
        { name: "Darknet Diaries", host: "Jack Rhysider", description: "True stories from the dark side of the internet", link: "https://darknetdiaries.com" },
        { name: "The Privacy Advisor Podcast", host: "IAPP", description: "Privacy news and expert interviews", link: "https://iapp.org/news/privacy-advisor-podcast" },
        { name: "Surveillance Report", host: "Techlore & The New Oil", description: "Weekly privacy news roundup", link: "https://surveillancereport.tech" }
      ]
    },
    {
      type: "Newsletters",
      icon: Mail,
      items: [
        { name: "TLDR Sec", host: "Clint Gibler", description: "Weekly security newsletter", link: "https://tldrsec.com" },
        { name: "Privacy Digest", host: "Didomi", description: "Data privacy news and updates", link: "https://didomi.io/newsletter" },
        { name: "The New Oil", host: "Nate", description: "Beginner-friendly privacy tips", link: "https://thenewoil.org" },
        { name: "A Little Privacy, Please", host: "Julie Rubash", description: "US privacy law updates", link: "https://sourcepoint.com/blog" }
      ]
    },
    {
      type: "YouTube Channels",
      icon: Video,
      items: [
        { name: "Techlore", host: "Henry", description: "Privacy education and tutorials", link: "https://youtube.com/@techlore" },
        { name: "The Hated One", host: "Anonymous", description: "Privacy and security analysis", link: "https://youtube.com/@TheHatedOne" },
        { name: "Rob Braxman Tech", host: "Rob Braxman", description: "De-Googling and privacy", link: "https://youtube.com/@robbraxmantech" },
        { name: "Mental Outlaw", host: "Kenny", description: "Linux and privacy tutorials", link: "https://youtube.com/@MentalOutlaw" }
      ]
    },
    {
      type: "Organizations",
      icon: Building,
      items: [
        { name: "Electronic Frontier Foundation (EFF)", host: "", description: "Digital rights advocacy", link: "https://eff.org" },
        { name: "IAPP", host: "", description: "International privacy professionals", link: "https://iapp.org" },
        { name: "Privacy International", host: "", description: "Global privacy research", link: "https://privacyinternational.org" },
        { name: "Access Now", host: "", description: "Digital rights for all", link: "https://accessnow.org" }
      ]
    }
  ];

  // Emerging Threats
  const emergingThreats = [
    {
      title: "Fog Reveal & Location Data Sales",
      description: "Government agencies purchasing location data from apps without warrants, tracking millions of citizens.",
      severity: "critical",
      protection: "Reset advertising ID monthly, disable location for non-essential apps"
    },
    {
      title: "Cross-App SDK Tracking",
      description: "Apps share data through embedded SDKs, building profiles across services without your consent.",
      severity: "high",
      protection: "Use App Tracking Transparency (iOS), review app permissions regularly"
    },
    {
      title: "AI-Powered Surveillance",
      description: "Facial recognition and behavioral analysis AI being deployed in public spaces.",
      severity: "high",
      protection: "Be aware of cameras, consider privacy clothing/accessories in sensitive areas"
    },
    {
      title: "Data Broker Aggregation",
      description: "Companies like Acxiom compile detailed profiles from hundreds of sources.",
      severity: "medium",
      protection: "Use data removal services, opt out from major brokers"
    }
  ];

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'Intermediate': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'Advanced': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const filteredConcepts = privacyConcepts.filter(c => 
    c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="education-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#00FF94]/10 border border-[#00FF94]/20 mb-4">
            <BookOpen className="w-8 h-8 text-[#00FF94]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Privacy <span className="text-[#00FF94]">Education Hub</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Comprehensive resources to understand digital privacy, protect yourself, and stay informed about the latest threats and regulations.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search topics, tools, laws..."
              className="pl-10 bg-[#0a0a0a] border-[#262626] text-white"
            />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#0a0a0a] border border-[#262626] p-1 flex flex-wrap justify-center gap-1">
            <TabsTrigger value="concepts" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black text-xs sm:text-sm">
              <BookOpen className="w-4 h-4 mr-1 sm:mr-2" />
              Privacy 101
            </TabsTrigger>
            <TabsTrigger value="tools" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black text-xs sm:text-sm">
              <Shield className="w-4 h-4 mr-1 sm:mr-2" />
              Tools
            </TabsTrigger>
            <TabsTrigger value="laws" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black text-xs sm:text-sm">
              <Scale className="w-4 h-4 mr-1 sm:mr-2" />
              Laws
            </TabsTrigger>
            <TabsTrigger value="threats" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black text-xs sm:text-sm">
              <AlertTriangle className="w-4 h-4 mr-1 sm:mr-2" />
              Threats
            </TabsTrigger>
            <TabsTrigger value="resources" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black text-xs sm:text-sm">
              <Newspaper className="w-4 h-4 mr-1 sm:mr-2" />
              Resources
            </TabsTrigger>
          </TabsList>

          {/* Privacy 101 Tab */}
          <TabsContent value="concepts" className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredConcepts.map((concept, index) => (
                <Card 
                  key={index} 
                  className="bg-[#0a0a0a] border-[#262626] hover:border-[#00FF94]/30 transition-colors cursor-pointer"
                  onClick={() => setSelectedConcept(concept)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
                        <concept.icon className="w-5 h-5 text-[#00FF94]" />
                      </div>
                      <Badge className={getDifficultyColor(concept.difficulty)}>
                        {concept.difficulty}
                      </Badge>
                    </div>
                    <h3 className="text-white font-semibold mb-2">{concept.title}</h3>
                    <p className="text-sm text-gray-500 mb-3">{concept.description}</p>
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>{concept.readTime} read</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Tools Tab */}
          <TabsContent value="tools" className="space-y-6">
            {toolCategories.map((category, catIndex) => (
              <Card key={catIndex} className="bg-[#0a0a0a] border-[#262626]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <category.icon className="w-5 h-5 text-[#00FF94]" />
                    {category.category}
                  </CardTitle>
                  <CardDescription className="text-gray-500">
                    {category.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {category.tools.map((tool, toolIndex) => (
                      <a
                        key={toolIndex}
                        href={tool.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#171717] rounded-lg p-4 hover:bg-[#1a1a1a] transition-colors group"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium text-sm">{tool.name}</span>
                          <ExternalLink className="w-3 h-3 text-gray-600 group-hover:text-[#00FF94]" />
                        </div>
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`w-3 h-3 ${i < Math.floor(tool.rating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-700'}`} 
                            />
                          ))}
                          <span className="text-xs text-gray-600 ml-1">{tool.rating}</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">{tool.highlight}</p>
                        <Badge className="bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20 text-xs">
                          {tool.price}
                        </Badge>
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Laws Tab */}
          <TabsContent value="laws" className="space-y-6">
            <div className="bg-[#00FF94]/5 border border-[#00FF94]/20 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-400">
                <strong className="text-[#00FF94]">Note:</strong> Privacy laws are constantly evolving. 
                As of 2025, 20+ US states have enacted comprehensive privacy laws, with more expected in 2026.
              </p>
            </div>
            
            {privacyLaws.map((region, regionIndex) => (
              <Card key={regionIndex} className="bg-[#0a0a0a] border-[#262626]">
                <CardHeader>
                  <CardTitle className="text-white text-lg">{region.region}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {region.laws.map((law, lawIndex) => (
                      <div key={lawIndex} className="bg-[#171717] rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="text-[#00FF94] font-bold text-lg">{law.name}</span>
                            <p className="text-xs text-gray-500">{law.fullName}</p>
                          </div>
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                            {law.year}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-400 mb-2">{law.description}</p>
                        <p className="text-xs text-gray-600">Jurisdiction: {law.jurisdiction}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Threats Tab */}
          <TabsContent value="threats" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              {emergingThreats.map((threat, index) => (
                <Card key={index} className="bg-[#0a0a0a] border-[#262626]">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        threat.severity === 'critical' ? 'bg-red-500/20' :
                        threat.severity === 'high' ? 'bg-orange-500/20' : 'bg-yellow-500/20'
                      }`}>
                        <AlertTriangle className={`w-5 h-5 ${
                          threat.severity === 'critical' ? 'text-red-400' :
                          threat.severity === 'high' ? 'text-orange-400' : 'text-yellow-400'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-semibold">{threat.title}</h3>
                          <Badge className={
                            threat.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                            threat.severity === 'high' ? 'bg-orange-500/20 text-orange-400' : 
                            'bg-yellow-500/20 text-yellow-400'
                          }>
                            {threat.severity}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">{threat.description}</p>
                    <div className="bg-[#00FF94]/5 border border-[#00FF94]/20 rounded-lg p-3">
                      <p className="text-xs text-[#00FF94] font-medium mb-1">How to Protect Yourself:</p>
                      <p className="text-xs text-gray-400">{threat.protection}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Link to="/cybershield">
              <Card className="bg-gradient-to-r from-[#00FF94]/10 to-transparent border-[#00FF94]/20 cursor-pointer hover:border-[#00FF94]/40 transition-colors">
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Shield className="w-10 h-10 text-[#00FF94]" />
                    <div>
                      <h3 className="text-white font-bold">Scan Your Device with CyberShield</h3>
                      <p className="text-sm text-gray-500">Check for tracking apps, location leaks, and privacy risks</p>
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 text-[#00FF94]" />
                </CardContent>
              </Card>
            </Link>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources" className="space-y-6">
            {learningResources.map((resource, resIndex) => (
              <Card key={resIndex} className="bg-[#0a0a0a] border-[#262626]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <resource.icon className="w-5 h-5 text-[#00FF94]" />
                    {resource.type}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {resource.items.map((item, itemIndex) => (
                      <a
                        key={itemIndex}
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#171717] rounded-lg p-4 hover:bg-[#1a1a1a] transition-colors group"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium">{item.name}</span>
                          <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-[#00FF94]" />
                        </div>
                        {item.host && (
                          <p className="text-xs text-[#00FF94] mb-1">by {item.host}</p>
                        )}
                        <p className="text-sm text-gray-500">{item.description}</p>
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* External Resources */}
            <Card className="bg-[#0a0a0a] border-[#262626]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#00FF94]" />
                  More Resources
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  <a
                    href="https://www.privacytools.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#171717] rounded-lg p-4 hover:bg-[#1a1a1a] transition-colors group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">PrivacyTools.io</span>
                      <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-[#00FF94]" />
                    </div>
                    <p className="text-sm text-gray-500">Comprehensive privacy software recommendations since 2015</p>
                  </a>
                  <a
                    href="https://www.didomi.io/blog/best-data-privacy-resources-2026"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#171717] rounded-lg p-4 hover:bg-[#1a1a1a] transition-colors group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">Didomi Privacy Resources</span>
                      <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-[#00FF94]" />
                    </div>
                    <p className="text-sm text-gray-500">Best data privacy resources for 2026</p>
                  </a>
                  <a
                    href="https://ssd.eff.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#171717] rounded-lg p-4 hover:bg-[#1a1a1a] transition-colors group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">EFF Surveillance Self-Defense</span>
                      <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-[#00FF94]" />
                    </div>
                    <p className="text-sm text-gray-500">Tips, tools and how-tos for safer online communications</p>
                  </a>
                  <a
                    href="https://prism-break.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#171717] rounded-lg p-4 hover:bg-[#1a1a1a] transition-colors group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">PRISM Break</span>
                      <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-[#00FF94]" />
                    </div>
                    <p className="text-sm text-gray-500">Opt out of global data surveillance programs</p>
                  </a>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* CTA */}
        {!user?.is_premium && (
          <Card className="bg-gradient-to-r from-[#00FF94]/10 to-blue-500/10 border-[#00FF94]/20 mt-8">
            <CardContent className="p-6 text-center">
              <Crown className="w-10 h-10 text-[#00FF94] mx-auto mb-3" />
              <h3 className="text-xl font-bold text-white mb-2">Get Premium Privacy Protection</h3>
              <p className="text-gray-400 text-sm mb-4 max-w-md mx-auto">
                Upgrade for breach monitoring, data removal services, and advanced privacy tools.
              </p>
              <Link to="/pricing">
                <Button className="btn-primary">
                  Upgrade to Premium
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Article Modal */}
      {selectedConcept && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedConcept(null)}
        >
          <div 
            className="bg-[#0a0a0a] border border-[#262626] rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#0a0a0a] border-b border-[#262626] p-6 flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center flex-shrink-0">
                  <selectedConcept.icon className="w-6 h-6 text-[#00FF94]" />
                </div>
                <div>
                  <h2 className="text-2xl font-heading font-bold text-white mb-2">{selectedConcept.title}</h2>
                  <div className="flex items-center gap-3 text-sm">
                    <Badge className={getDifficultyColor(selectedConcept.difficulty)}>
                      {selectedConcept.difficulty}
                    </Badge>
                    <span className="text-gray-500">{selectedConcept.readTime} read</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedConcept(null)}
                className="text-gray-500 hover:text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="prose prose-invert prose-green max-w-none">
                {selectedConcept.content ? (
                  selectedConcept.content.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-gray-400 mb-4 leading-relaxed whitespace-pre-wrap">
                      {paragraph}
                    </p>
                  ))
                ) : (
                  <p className="text-gray-400 leading-relaxed">
                    {selectedConcept.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Add missing icon
const Trash2 = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M3 6h18"/>
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
    <line x1="10" x2="10" y1="11" y2="17"/>
    <line x1="14" x2="14" y1="11" y2="17"/>
  </svg>
);
